import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Cloud,
  CloudOff,
  Download,
  FileText,
  Lock,
  MessageSquarePlus,
  Send,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Chip labels — EXACT wording per section.
const SERVICE_PULSE = [
  { value: "thriving", label: "Thriving", tone: "blue" },
  { value: "healthy", label: "Healthy", tone: "green" },
  { value: "struggles", label: "Struggles", tone: "amber" },
  { value: "issues", label: "Issues", tone: "red" },
];
const LEADER_PULSE = [
  { value: "thriving", label: "Thriving", tone: "blue" },
  { value: "healthy", label: "Healthy", tone: "green" },
  { value: "stretched", label: "Stretched", tone: "amber" },
  { value: "depleted", label: "Depleted", tone: "red" },
];
const HOT_POTATO_LABELS = {
  facilities: "Facilities",
  security: "Security",
  budget: "Budget",
  decision: "Decision",
  staff_volunteer: "Staff / Volunteer",
};
const HOT_POTATO_ORDER = ["facilities", "security", "budget", "decision", "staff_volunteer"];

const TONE_CLASS = {
  blue: "bg-[#3F7AD8] text-cream-100 border-[#3F7AD8]",
  green: "bg-forest-600 text-cream-100 border-forest-600",
  amber: "bg-[#D19A2E] text-cream-100 border-[#D19A2E]",
  red: "bg-terracotta-500 text-cream-100 border-terracotta-500",
};

const emptyForm = () => ({
  service_pulse: null,
  service_pulse_narrative: "",
  leader_pulse: null,
  leader_pulse_narrative: "",
  hot_potatoes: HOT_POTATO_ORDER.reduce(
    (acc, k) => ({ ...acc, [k]: { flag: false, description: "" } }),
    {}
  ),
  commitments: [
    { text: "" },
    { text: "" },
    { text: "" },
  ],
});

function PulseChipRow({ options, value, onChange, testIdPrefix, disabled }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-row`}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            data-testid={`${testIdPrefix}-${opt.value}`}
            className={`min-h-[44px] px-4 rounded-full border-2 text-sm font-medium transition-all ${
              active
                ? TONE_CLASS[opt.tone] + " shadow-md"
                : "bg-cream-100 border-sage-300 text-charcoal hover:border-forest-600"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function SaveStatus({ state, savedAt }) {
  if (state === "saving")
    return (
      <span className="inline-flex items-center gap-1 text-xs text-charcoal-muted" data-testid="ww-save-status-saving">
        <Cloud className="h-3.5 w-3.5 animate-pulse" strokeWidth={1.5} /> Saving draft…
      </span>
    );
  if (state === "saved")
    return (
      <span className="inline-flex items-center gap-1 text-xs text-forest-700" data-testid="ww-save-status-saved">
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        {savedAt ? `Draft saved · ${new Date(savedAt).toLocaleTimeString()}` : "Draft saved"}
      </span>
    );
  if (state === "error")
    return (
      <span className="inline-flex items-center gap-1 text-xs text-terracotta-600" data-testid="ww-save-status-error">
        <CloudOff className="h-3.5 w-3.5" strokeWidth={1.5} /> Couldn&rsquo;t save draft
      </span>
    );
  return <span className="text-xs italic text-charcoal-muted" data-testid="ww-save-status-idle">Autosave on</span>;
}

export default function WeeklyWinsSubmit() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [goal, setGoal] = useState(null);
  const [lastWeekCommitments, setLastWeekCommitments] = useState([]);
  const [priorStatuses, setPriorStatuses] = useState({});
  const [form, setForm] = useState(emptyForm());
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [note, setNote] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const saveTimer = useRef(null);
  const hydrating = useRef(true);

  const isLocked = report?.status === "submitted";

  const hydrateForm = (r) => {
    if (!r) return;
    setForm({
      service_pulse: r.service_pulse || null,
      service_pulse_narrative: r.service_pulse_narrative || "",
      leader_pulse: r.leader_pulse || null,
      leader_pulse_narrative: r.leader_pulse_narrative || "",
      hot_potatoes: HOT_POTATO_ORDER.reduce((acc, k) => {
        const row = (r.hot_potatoes || {})[k] || {};
        return {
          ...acc,
          [k]: { flag: !!row.flag, description: row.description || "" },
        };
      }, {}),
      commitments: (r.commitments || [{}, {}, {}]).slice(0, 3).concat(
        Array.from({ length: Math.max(0, 3 - (r.commitments || []).length) }, () => ({ text: "" }))
      ).map((cm) => ({ id: cm.id || null, text: cm.text || "" })),
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/weekly-wins/mine/current");
      setReport(data.report);
      setGoal(data.goal);
      setLastWeekCommitments(data.last_week_commitments || []);
      setPriorStatuses(
        (data.last_week_commitments || []).reduce((acc, c) => {
          if (c.id) acc[c.id] = c.prior_week_status || "";
          return acc;
        }, {}),
      );
      hydrateForm(data.report);
      setLastSavedAt(data.report?.updated_at);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load your report.");
      }
    } finally {
      setLoading(false);
      // small delay so the initial hydrate doesn't kick off a save
      setTimeout(() => { hydrating.current = false; }, 50);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-save debounce
  useEffect(() => {
    if (loading || isLocked || hydrating.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(async () => {
      try {
        const payload = buildDraftPayload(form);
        const { data } = await api.put("/weekly-wins/mine/current", payload);
        setReport(data);
        setLastSavedAt(data.updated_at);
        setSaveState("saved");
      } catch (err) {
        setSaveState("error");
      }
    }, 1200);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loading, isLocked]);

  const submitReport = async () => {
    setSubmitting(true);
    try {
      const statuses = {};
      for (const cm of lastWeekCommitments) {
        if (cm.id && priorStatuses[cm.id]) statuses[cm.id] = priorStatuses[cm.id];
      }
      const payload = {
        ...buildDraftPayload(form),
        prior_commitment_statuses: statuses,
      };
      const { data } = await api.post("/weekly-wins/mine/current/submit", payload);
      setReport(data);
      hydrateForm(data);
      toast.success("Weekly Wins submitted. Thank you.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const addNote = async () => {
    const v = note.trim();
    if (!v) return;
    try {
      const { data } = await api.post(`/weekly-wins/reports/${report.id}/note`, { note: v });
      setReport(data);
      setNote("");
      setAddingNote(false);
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const downloadPdf = async () => {
    if (!report?.id) return;
    try {
      const res = await api.get(`/weekly-wins/reports/${report.id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `weekly-wins-${report.campus_name}-${report.week_start}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't download the PDF.");
    }
  };

  const flaggedCount = useMemo(
    () => HOT_POTATO_ORDER.filter((k) => form.hot_potatoes[k]?.flag).length,
    [form]
  );
  const needsLeaderNarrative =
    form.leader_pulse === "stretched" || form.leader_pulse === "depleted";
  const commitmentsFilled = form.commitments.filter((c) => c.text.trim()).length;
  const priorStatusesComplete = lastWeekCommitments
    .filter((c) => (c.text || "").trim())
    .every((c) => ["done", "in_progress", "dropped"].includes(priorStatuses[c.id]));
  const canSubmit =
    !isLocked &&
    form.service_pulse &&
    (form.service_pulse_narrative || "").trim() &&
    form.leader_pulse &&
    (!needsLeaderNarrative || (form.leader_pulse_narrative || "").trim()) &&
    HOT_POTATO_ORDER.every(
      (k) => !form.hot_potatoes[k].flag || form.hot_potatoes[k].description.trim()
    ) &&
    commitmentsFilled > 0 &&
    priorStatusesComplete;

  if (loading) {
    return (
      <div className="patria-card p-8" data-testid="ww-loading">
        <p className="italic text-charcoal-muted">Loading this week&rsquo;s report…</p>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="max-w-2xl mx-auto patria-card p-8 text-center" data-testid="ww-forbidden">
        <div className="inline-flex items-center gap-2 patria-chip-terra text-xs">
          <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.5} /> Access Restricted
        </div>
        <h1 className="mt-3 text-3xl font-serif text-forest-900">
          Weekly Wins is for Campus Pastors
        </h1>
        <p className="mt-2 text-sm text-charcoal-soft">
          This surface is scoped to Campus Pastors submitting on behalf of their campus.
          If you believe you should have access, ask People Operations.
        </p>
        <Link to="/rhythm" className="patria-btn-primary mt-6 inline-flex" data-testid="btn-ww-forbidden-back">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Back To RHYTHM
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="weekly-wins-submit">
      <div>
        <Link to="/rhythm" className="text-sm text-forest-700 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To RHYTHM
        </Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
              Weekly Wins · Campus Health Briefing
            </p>
            <h1 className="text-4xl font-serif text-forest-900">
              {report?.campus_name || "Your Campus"}
            </h1>
            <p className="text-sm text-charcoal-soft mt-1">
              Week of {report?.week_start} → {report?.week_end}
              {report?.is_late && (
                <span className="ml-2 patria-chip-terra text-[10px]" data-testid="ww-late-chip">LATE</span>
              )}
            </p>
            {(report?.editor_names || []).length > 1 && (
              <p className="text-[11px] text-charcoal-muted mt-1" data-testid="ww-co-editors">
                Co-edited with {report.editor_names.filter((n) => n !== user?.name).join(", ")}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            {isLocked ? (
              <span className="patria-chip-green" data-testid="ww-locked-chip">
                <Lock className="h-3 w-3 mr-1" strokeWidth={2} /> Submitted · Locked
              </span>
            ) : (
              <SaveStatus state={saveState} savedAt={lastSavedAt} />
            )}
            {report?.id && (
              <button
                type="button"
                onClick={downloadPdf}
                className="patria-btn-ghost text-xs min-h-[44px]"
                data-testid="btn-ww-pdf"
              >
                <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {/* The Goal ribbon */}
      {goal && (
        <div
          className="patria-card p-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-l-4 border-terracotta-500"
          data-testid="ww-goal-ribbon"
        >
          <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
            The Goal
          </p>
          <p className="text-sm text-charcoal">
            Improve our Overall Campus Health Index from{" "}
            <strong className="text-forest-900">{goal.current_score}</strong> to{" "}
            <strong className="text-forest-900">{goal.target_score}</strong> by{" "}
            <strong className="text-forest-900">{goal.deadline}</strong>.
          </p>
        </div>
      )}

      {/* Follow-through loop — prior-week commitments */}
      {lastWeekCommitments.length > 0 && !isLocked && (
        <section className="patria-card p-6" data-testid="ww-prior-week">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-forest-900">Last Week&rsquo;s Commitments</h2>
          </div>
          <p className="text-xs text-charcoal-muted italic mb-4">
            Mark each of last week&rsquo;s commitments as{" "}
            <strong>Done</strong>, <strong>In progress</strong>, or <strong>Dropped</strong>{" "}
            before submitting this week&rsquo;s report.
          </p>
          <ul className="space-y-3">
            {lastWeekCommitments.map((c, i) => (
              <li key={c.id || i} className="p-3 rounded-xl bg-sage-200/40" data-testid={`ww-prior-${i}`}>
                <p className="text-forest-900 text-sm">&ldquo;{c.text}&rdquo;</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { v: "done", l: "Done", tone: "green" },
                    { v: "in_progress", l: "In progress", tone: "amber" },
                    { v: "dropped", l: "Dropped", tone: "red" },
                  ].map((opt) => {
                    const active = priorStatuses[c.id] === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() =>
                          setPriorStatuses({ ...priorStatuses, [c.id]: opt.v })
                        }
                        data-testid={`ww-prior-${i}-${opt.v}`}
                        className={`min-h-[40px] px-3 rounded-full border-2 text-xs font-medium ${
                          active
                            ? TONE_CLASS[opt.tone] + " shadow-sm"
                            : "bg-cream-100 border-sage-300 text-charcoal-soft"
                        }`}
                      >
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Section 1 — Service(s) Pulse */}
      <section className="patria-card p-6" data-testid="ww-section-service">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          Section 1
        </p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Service(s) Pulse</h2>
        <p className="text-xs text-charcoal-muted italic mt-1">
          How did the weekend feel? Choose the label that most honestly fits.
        </p>
        <div className="mt-4">
          <PulseChipRow
            options={SERVICE_PULSE}
            value={form.service_pulse}
            onChange={(v) => setForm({ ...form, service_pulse: v })}
            testIdPrefix="ww-service-pulse"
            disabled={isLocked}
          />
        </div>
        <label className="block mt-4 text-xs uppercase tracking-widest text-charcoal-muted">
          Narrative — What Happened?
        </label>
        <textarea
          className="patria-input w-full min-h-[120px] mt-1"
          placeholder="A brief, honest overview of what happened this weekend…"
          value={form.service_pulse_narrative}
          onChange={(e) => setForm({ ...form, service_pulse_narrative: e.target.value })}
          data-testid="ww-service-narrative"
          disabled={isLocked}
        />
      </section>

      {/* Section 2 — Leader Pulse */}
      <section className="patria-card p-6 border-l-4 border-forest-600" data-testid="ww-section-leader">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
          <p className="text-[10px] uppercase tracking-widest text-forest-700 font-medium">
            Section 2 · Private To You & Reviewer
          </p>
        </div>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Leader Pulse</h2>
        <p className="text-xs text-charcoal-muted italic mt-1">
          Only you, our Reviewer, and People Operations see this. Please be honest — this is
          where we can genuinely support you.
        </p>
        <div className="mt-4">
          <PulseChipRow
            options={LEADER_PULSE}
            value={form.leader_pulse}
            onChange={(v) => setForm({ ...form, leader_pulse: v })}
            testIdPrefix="ww-leader-pulse"
            disabled={isLocked}
          />
        </div>
        <label className="block mt-4 text-xs uppercase tracking-widest text-charcoal-muted">
          Narrative
          {needsLeaderNarrative && (
            <span className="ml-2 patria-chip-terra text-[10px]">Required When Stretched Or Depleted</span>
          )}
        </label>
        <textarea
          className="patria-input w-full min-h-[120px] mt-1"
          placeholder="What&rsquo;s happening for you personally? What kind of support would help?"
          value={form.leader_pulse_narrative}
          onChange={(e) => setForm({ ...form, leader_pulse_narrative: e.target.value })}
          data-testid="ww-leader-narrative"
          disabled={isLocked}
        />
      </section>

      {/* Section 3 — Hot Potatoes */}
      <section className="patria-card p-6" data-testid="ww-section-hot-potatoes">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          Section 3
        </p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Hot Potatoes</h2>
        <p className="text-xs text-charcoal-muted italic mt-1">
          Anything that needs a decision, a heads-up, or urgent support. Flag the category and
          share the details.
        </p>
        <div className="mt-4 space-y-3">
          {HOT_POTATO_ORDER.map((k) => {
            const row = form.hot_potatoes[k];
            return (
              <div
                key={k}
                className={`p-4 rounded-xl border-2 ${
                  row.flag ? "border-terracotta-500 bg-terracotta-500/5" : "border-sage-300 bg-cream-100"
                }`}
                data-testid={`ww-hp-${k}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-forest-900">{HOT_POTATO_LABELS[k]}</p>
                    <p className="text-[11px] text-charcoal-muted">
                      {row.flag ? "Flagged this week" : "Nothing to flag"}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={row.flag}
                    onClick={() =>
                      setForm({
                        ...form,
                        hot_potatoes: {
                          ...form.hot_potatoes,
                          [k]: { ...row, flag: !row.flag },
                        },
                      })
                    }
                    disabled={isLocked}
                    data-testid={`ww-hp-${k}-toggle`}
                    className={`relative min-h-[44px] min-w-[68px] w-[68px] rounded-full transition flex items-center ${
                      row.flag ? "bg-terracotta-500" : "bg-sage-300"
                    } disabled:opacity-60`}
                  >
                    <span
                      className={`absolute top-1 left-1 h-9 w-9 rounded-full bg-cream-100 shadow transition ${
                        row.flag ? "translate-x-6" : ""
                      }`}
                    />
                  </button>
                </div>
                {row.flag && (
                  <textarea
                    className="patria-input w-full min-h-[80px] mt-3"
                    placeholder={`Describe the ${HOT_POTATO_LABELS[k]} issue and any support needed…`}
                    value={row.description}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        hot_potatoes: {
                          ...form.hot_potatoes,
                          [k]: { ...row, description: e.target.value },
                        },
                      })
                    }
                    data-testid={`ww-hp-${k}-desc`}
                    disabled={isLocked}
                  />
                )}
                {k === "staff_volunteer" && (
                  <p
                    className="mt-3 text-[11px] text-terracotta-700 italic leading-snug"
                    data-testid="ww-hp-staff-er-helper"
                  >
                    If this involves harassment, discrimination, safety, or another
                    reportable concern, also file it through the ER Center — this
                    report does not replace that.
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {flaggedCount > 0 && (
          <p className="mt-3 text-xs text-terracotta-700 inline-flex items-center gap-1" data-testid="ww-hp-summary">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
            {flaggedCount} hot potato{flaggedCount === 1 ? "" : "es"} flagged this week
          </p>
        )}
      </section>

      {/* Section 4 — Commitments */}
      <section className="patria-card p-6" data-testid="ww-section-commitments">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          Section 4
        </p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Weekly Wins Commitments</h2>
        <p className="text-xs text-charcoal-muted italic mt-1">
          What are the 1–3 concrete moves you&rsquo;re making this week? You&rsquo;ll
          report progress on each of these next Sunday.
        </p>
        <ul className="mt-4 space-y-3">
          {form.commitments.map((cm, i) => (
            <li key={i} data-testid={`ww-commitment-${i}`}>
              <label className="block text-xs uppercase tracking-widest text-charcoal-muted mb-1">
                Commitment {i + 1}
              </label>
              <input
                type="text"
                className="patria-input w-full"
                placeholder={`e.g., ${i === 0 ? "Meet with worship team for rotation reset" : i === 1 ? "Onboard new usher lead" : "Send thank-you notes to volunteers"}`}
                value={cm.text}
                onChange={(e) => {
                  const next = [...form.commitments];
                  next[i] = { ...next[i], text: e.target.value };
                  setForm({ ...form, commitments: next });
                }}
                data-testid={`ww-commitment-${i}-text`}
                disabled={isLocked}
                maxLength={280}
              />
            </li>
          ))}
        </ul>
      </section>

      {/* Post-submission notes */}
      {isLocked && (
        <section className="patria-card p-6" data-testid="ww-notes-section">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-serif text-2xl text-forest-900">Post-Submission Notes</h2>
            {!addingNote && (
              <button
                type="button"
                className="patria-btn-secondary text-sm"
                onClick={() => setAddingNote(true)}
                data-testid="btn-ww-add-note"
              >
                <MessageSquarePlus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Note
              </button>
            )}
          </div>
          <ul className="mt-3 space-y-3">
            {(report?.post_submission_notes || []).map((n, i) => (
              <li key={i} className="p-3 rounded-xl bg-sage-200/40" data-testid={`ww-note-${i}`}>
                <p className="text-xs text-charcoal-muted">
                  {n.by_user_name} · {(n.at || "").slice(0, 19).replace("T", " ")}
                </p>
                <p className="text-forest-900 text-sm mt-1 whitespace-pre-wrap">{n.note}</p>
              </li>
            ))}
            {(report?.post_submission_notes || []).length === 0 && (
              <li className="text-xs italic text-charcoal-muted">No notes yet.</li>
            )}
          </ul>
          {addingNote && (
            <div className="mt-3 space-y-2">
              <textarea
                className="patria-input w-full min-h-[100px]"
                placeholder="Anything to add after the fact?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="ww-note-textarea"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="patria-btn-primary text-sm"
                  onClick={addNote}
                  disabled={!note.trim()}
                  data-testid="btn-ww-save-note"
                >
                  Save Note
                </button>
                <button
                  type="button"
                  className="patria-btn-ghost text-sm"
                  onClick={() => { setAddingNote(false); setNote(""); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Sticky action bar */}
      {!isLocked && (
        <div
          className="sticky bottom-4 z-30 patria-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-lg"
          data-testid="ww-action-bar"
        >
          <div className="flex-1 min-w-0">
            <p className="text-xs text-charcoal-muted">
              {canSubmit
                ? "Ready to submit. Once you submit, this week's report locks."
                : lastWeekCommitments.length > 0 && !priorStatusesComplete
                ? "Mark each of last week's commitments before submitting."
                : "Complete each section to enable submission."}
            </p>
            <SaveStatus state={saveState} savedAt={lastSavedAt} />
          </div>
          <button
            type="button"
            className="patria-btn-primary min-h-[48px] w-full sm:w-auto"
            onClick={submitReport}
            disabled={!canSubmit || submitting}
            data-testid="btn-ww-submit"
          >
            <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {submitting ? "Submitting…" : "Submit This Week's Report"}
          </button>
        </div>
      )}
    </div>
  );
}

function buildDraftPayload(form) {
  return {
    service_pulse: form.service_pulse || null,
    service_pulse_narrative: form.service_pulse_narrative || "",
    leader_pulse: form.leader_pulse || null,
    leader_pulse_narrative: form.leader_pulse_narrative || "",
    hot_potatoes: HOT_POTATO_ORDER.reduce((acc, k) => {
      const row = form.hot_potatoes[k];
      return {
        ...acc,
        [k]: {
          flag: !!row.flag,
          description: row.flag ? (row.description || "").trim() : null,
        },
      };
    }, {}),
    commitments: form.commitments.map((cm) => {
      const out = { text: (cm.text || "").trim() };
      if (cm.id) out.id = cm.id;   // preserve stable ID so backend doesn't reshuffle
      return out;
    }),
  };
}
